import uid from "common/utils/uid";
import type { DocMigration } from "../docMigration";

// eslint-disable-next-line @typescript-eslint/naming-convention
const migration_0_0_162: DocMigration = {
  maxSchemaVersion: "0.0.162",
  description: "Migration Foundation Settings to Foundation Catalog",
  apply(json: any) {
    // update naming on foundationPatterns
    if (json.roomLayouts) {
      for (const layoutId in json.roomLayouts) {
        const roomLayout = json.roomLayouts[layoutId];
        if (!roomLayout.footingPatterns) {
          continue;
        }
        if (!roomLayout.footingPatterns.length) {
          delete roomLayout["footingPatterns"];
          continue;
        }

        // array of type any to avoid imporing types in migration files
        const foundationPatterns: any[] = [];

        for (const footingPattern of roomLayout.footingPatterns) {
          const internalFootingPattern = footingPattern.footingPattern;
          internalFootingPattern.patternType = internalFootingPattern.footingType;
          delete internalFootingPattern.footingType;

          foundationPatterns.push({
            ...footingPattern,
            name: (footingPattern.name as string).replace("Footing", "Foundation"),
            foundationPattern: internalFootingPattern,
          });
        }
        roomLayout.foundationPatterns = foundationPatterns;
        delete roomLayout.footingPatterns;
      }
    }

    // update foundation settings
    if (json.settings) {
      const components = {};
      const assemblies = {};

      const spanFoundationConditions = [
        "INTERIOR_INTERIOR",
        "BASEMENT_BASEMENT",
        "GARAGE_GARAGE",
        "PORCH_PORCH",
        "DECK_DECK",
      ];

      const allSettingsIds = Object.keys(json.settings);
      allSettingsIds.sort((a, b) => json.settings[a].sortIndex - json.settings[b].sortIndex);

      const assemblyIds = {};
      const spanAssemblyIds = {};

      for (const settingId of allSettingsIds) {
        const setting = json.settings[settingId];

        const parentName: string = json.settingsSets?.[setting.parentId]?.name;

        if (setting.type === "FOUNDATION_SETTINGS") {
          const hash = setting.foundationHash;
          if (!hash) {
            continue;
          }

          const systemSetting = setting[hash];

          if (!systemSetting) {
            continue;
          }

          const existingAssemblyId = assemblyIds[hash];
          const existingAssembly = assemblies[existingAssemblyId] ?? {};
          const existingSpanAssemblyId = spanAssemblyIds[hash];
          const existingSpanAssembly = assemblies[existingSpanAssemblyId] ?? {};

          const subSystem = hash.split(".")[1];

          if (systemSetting.brickLedgeType === "AT_FOOTING") {
            systemSetting.brickLedgeType = "ADD";
          }

          if (subSystem === "MONOLITHIC") {
            const existingFooting = components[existingAssembly.footingId] ?? {};

            const footingWidth = systemSetting.footingWidth ?? existingFooting.footingWidth;
            const brickLedgeType = systemSetting.brickLedgeType ?? existingFooting.brickLedgeType;
            const brickLedgeWidth = systemSetting.brickLedgeWidth ?? existingFooting.brickLedgeWidth;

            let footingId: string | undefined = undefined;

            if (footingWidth !== undefined || brickLedgeType !== undefined || brickLedgeWidth !== undefined) {
              const id = uid();
              const turndownFooting = {
                componentType: "FOOTING",
                footingType: "TURNDOWN",
                name: `Turndown Footing for ${parentName}`,
                footingWidth: footingWidth ?? 18,
                brickLedgeType: brickLedgeType ?? "ADD",
                brickLedgeWidth: brickLedgeWidth ?? 5.5,
                brickLedgeBehavior: "AUTO",
              };

              components[id] = turndownFooting;
              footingId = id;
            }

            const existingSpanFooting = components[existingSpanAssembly.footingId] ?? {};
            const spanFootingWidth = systemSetting.spanFootingWidth ?? existingSpanFooting.footingWidth;

            let spanFootingId: string | undefined = undefined;

            if (spanFootingWidth !== undefined && spanFootingWidth !== footingWidth) {
              const id = uid();
              const spanFooting = {
                componentType: "FOOTING",
                footingType: "TURNDOWN",
                name: `Span Footing for ${parentName}`,
                footingWidth: spanFootingWidth,
                brickLedgeType: brickLedgeType ?? "ADD",
                brickLedgeWidth: brickLedgeWidth ?? 5.5,
                brickLedgeBehavior: "AUTO",
              };

              components[id] = spanFooting;
              spanFootingId = id;
            }

            if (footingId) {
              const defaultAssemblyId = uid();
              const defaultAssembly = {
                assemblySystem: subSystem,
                name: `${parentName} Assembly`,
                footingId: footingId,
              };

              assemblies[defaultAssemblyId] = defaultAssembly;

              systemSetting.foundationAssembly = defaultAssemblyId;

              assemblyIds[hash] = defaultAssemblyId;
            }

            if (spanFootingId) {
              const spanAssemblyId = uid();
              const spanAssembly = {
                assemblySystem: subSystem,
                name: `${parentName} Span Assembly`,
                footingId: spanFootingId,
              };

              assemblies[spanAssemblyId] = spanAssembly;

              const foundationConditions = systemSetting.foundationConditions ?? {};

              for (const span of spanFoundationConditions) {
                const conditionData = foundationConditions[span] ?? {};
                conditionData.assemblyId = spanAssemblyId;
                foundationConditions[span] = conditionData;
              }

              systemSetting.foundationConditions = foundationConditions;

              spanAssemblyIds[hash] = spanAssemblyId;
            } else if (spanAssemblyIds[hash]) {
              const spanAssemblyId = spanAssemblyIds[hash];

              const foundationConditions = systemSetting.foundationConditions ?? {};

              for (const span of spanFoundationConditions) {
                const conditionData = foundationConditions[span] ?? {};
                conditionData.assemblyId = spanAssemblyId;
                foundationConditions[span] = conditionData;
              }

              systemSetting.foundationConditions = foundationConditions;
            }

            delete systemSetting.footingWidth;
            delete systemSetting.spanFootingWidth;
            delete systemSetting.brickLedgeType;
            delete systemSetting.brickLedgeWidth;
          }

          if (subSystem === "STEM_WALL_CIP") {
            const existingFooting = components[existingAssembly.footingId] ?? {};
            const existingStemWall = components[existingAssembly.stemWallId] ?? {};

            const footingWidth = systemSetting.footingWidth ?? existingFooting.footingWidth;
            const footingDepth = systemSetting.footingDepth ?? existingFooting.footingDepth;
            const brickLedgeFootingPadding = systemSetting.brickLedgeFootingPadding ?? existingFooting.footingPadding;

            const brickLedgeType = systemSetting.brickLedgeType ?? existingStemWall.brickLedgeType;
            const brickLedgeWidth = systemSetting.brickLedgeWidth ?? existingStemWall.brickLedgeWidth;
            const stemWallWidth = systemSetting.stemWallWidth ?? existingStemWall.stemWallWidth;

            let footingId: string | undefined = undefined;

            if (footingWidth !== undefined || footingDepth !== undefined || brickLedgeFootingPadding !== undefined) {
              const id = uid();
              const stripFooting = {
                componentType: "FOOTING",
                footingType: "STRIP",
                name: `Strip Footing for ${parentName}`,
                footingWidth: footingWidth ?? 18,
                footingDepth: footingDepth ?? 8,
                footingPadding: brickLedgeFootingPadding ?? 4,
              };

              components[id] = stripFooting;
              footingId = id;
            }

            const existingSpanFooting = components[existingSpanAssembly.footingId] ?? {};
            const spanFootingWidth = systemSetting.spanFootingWidth ?? existingSpanFooting.footingWidth;

            let spanFootingId: string | undefined = undefined;

            if (spanFootingWidth !== undefined && spanFootingWidth !== footingWidth) {
              const id = uid();
              const spanFooting = {
                componentType: "FOOTING",
                footingType: "STRIP",
                name: `Span Footing for ${parentName}`,
                footingWidth: spanFootingWidth,
                footingDepth: footingDepth ?? 8,
                footingPadding: brickLedgeFootingPadding ?? 4,
              };

              components[id] = spanFooting;
              spanFootingId = id;
            }

            let stemWallId: string | undefined = undefined;

            if (stemWallWidth !== undefined || brickLedgeType !== undefined || brickLedgeWidth !== undefined) {
              const id = uid();
              const stemWall = {
                componentType: "STEMWALL",
                stemWallType: "CIP",
                name: `CIP Stem Wall for ${parentName}`,
                stemWallWidth: stemWallWidth ?? 8,
                brickLedgeType: brickLedgeType ?? "ADD",
                brickLedgeWidth: brickLedgeWidth ?? 5.5,
                brickLedgeBehavior: "AUTO",
              };

              components[id] = stemWall;
              stemWallId = id;
            }

            if (footingId || stemWallId) {
              const defaultAssemblyId = uid();
              const defaultAssembly = {
                assemblySystem: subSystem,
                name: `${parentName} Assembly`,
                footingId: footingId ?? "defaultStripFooting",
                stemWallId: stemWallId ?? "defaultCIPStemWall",
              };

              assemblies[defaultAssemblyId] = defaultAssembly;

              systemSetting.foundationAssembly = defaultAssemblyId;

              assemblyIds[hash] = defaultAssemblyId;
            }

            if (spanFootingId) {
              const spanAssemblyId = uid();
              const spanAssembly = {
                assemblySystem: subSystem,
                name: `${parentName} Span Assembly`,
                footingId: spanFootingId,
                stemWallId: stemWallId ?? "defaultCIPStemWall",
              };

              assemblies[spanAssemblyId] = spanAssembly;

              const foundationConditions = systemSetting.foundationConditions ?? {};

              for (const span of spanFoundationConditions) {
                const conditionData = foundationConditions[span] ?? {};
                conditionData.assemblyId = spanAssemblyId;
                foundationConditions[span] = conditionData;
              }

              systemSetting.foundationConditions = foundationConditions;

              spanAssemblyIds[hash] = spanAssemblyId;
            } else if (spanAssemblyIds[hash]) {
              const spanAssemblyId = spanAssemblyIds[hash];

              const foundationConditions = systemSetting.foundationConditions ?? {};

              for (const span of spanFoundationConditions) {
                const conditionData = foundationConditions[span] ?? {};
                conditionData.assemblyId = spanAssemblyId;
                foundationConditions[span] = conditionData;
              }

              systemSetting.foundationConditions = foundationConditions;
            }

            delete systemSetting.footingWidth;
            delete systemSetting.spanFootingWidth;
            delete systemSetting.footingDepth;
            delete systemSetting.brickLedgeType;
            delete systemSetting.brickLedgeWidth;
            delete systemSetting.stemWallWidth;
            delete systemSetting.brickLedgeFootingPadding;
          }

          if (subSystem === "STEM_WALL_CMU") {
            const existingFooting = components[existingAssembly.footingId] ?? {};
            const existingStemWall = components[existingAssembly.stemWallId] ?? {};

            const footingWidth = systemSetting.footingWidth ?? existingFooting.footingWidth;
            const footingDepth = systemSetting.footingDepth ?? existingFooting.footingDepth;
            const brickLedgeFootingPadding = systemSetting.brickLedgeFootingPadding ?? existingFooting.footingPadding;

            const brickLedgeType = systemSetting.brickLedgeType ?? existingStemWall.brickLedgeType;
            const brickLedgeBlockType = systemSetting.brickLedgeBlockType ?? existingStemWall.brickLedgeBlockType;
            const stemWallBlockType = systemSetting.stemWallBlockType ?? existingStemWall.stemWallBlockType;

            let footingId: string | undefined = undefined;

            if (footingWidth !== undefined || footingDepth !== undefined || brickLedgeFootingPadding !== undefined) {
              const id = uid();
              const stripFooting = {
                componentType: "FOOTING",
                footingType: "STRIP",
                name: `Strip Footing for ${parentName}`,
                footingWidth: footingWidth ?? 18,
                footingDepth: footingDepth ?? 8,
                footingPadding: brickLedgeFootingPadding ?? 4,
              };

              components[id] = stripFooting;
              footingId = id;
            }

            const existingSpanFooting = components[existingSpanAssembly.footingId] ?? {};
            const spanFootingWidth = systemSetting.spanFootingWidth ?? existingSpanFooting.footingWidth;

            let spanFootingId: string | undefined = undefined;

            if (spanFootingWidth !== undefined && spanFootingWidth !== footingWidth) {
              const id = uid();
              const spanFooting = {
                componentType: "FOOTING",
                footingType: "STRIP",
                name: `Span Footing for ${parentName}`,
                footingWidth: spanFootingWidth,
                footingDepth: footingDepth ?? 8,
                footingPadding: brickLedgeFootingPadding ?? 4,
              };

              components[id] = spanFooting;
              spanFootingId = id;
            }

            let stemWallId: string | undefined = undefined;

            if (stemWallBlockType !== undefined || brickLedgeBlockType !== undefined || brickLedgeType !== undefined) {
              const id = uid();
              const stemWall = {
                componentType: "STEMWALL",
                stemWallType: "CMU",
                name: `CMU Stem Wall for ${parentName}`,
                stemWallBlockType: stemWallBlockType ?? "CMU_8_INCH",
                brickLedgeType: brickLedgeType ?? "ADD",
                brickLedgeBlockType: brickLedgeBlockType ?? "CMU_12_INCH",
                brickLedgeBehavior: "AUTO",
              };

              components[id] = stemWall;
              stemWallId = id;
            }

            if (footingId || stemWallId) {
              const defaultAssemblyId = uid();
              const defaultAssembly = {
                assemblySystem: subSystem,
                name: `${parentName} Assembly`,
                footingId: footingId ?? "defaultStripFooting",
                stemWallId: stemWallId ?? "defaultCMUStemWall",
              };

              assemblies[defaultAssemblyId] = defaultAssembly;

              systemSetting.foundationAssembly = defaultAssemblyId;

              assemblyIds[hash] = defaultAssemblyId;
            }

            if (spanFootingId) {
              const spanAssemblyId = uid();
              const spanAssembly = {
                assemblySystem: subSystem,
                name: `${parentName} Span Assembly`,
                footingId: spanFootingId,
                stemWallId: stemWallId ?? "defaultCMUStemWall",
              };

              assemblies[spanAssemblyId] = spanAssembly;

              const foundationConditions = systemSetting.foundationConditions ?? {};

              for (const span of spanFoundationConditions) {
                const conditionData = foundationConditions[span] ?? {};
                conditionData.assemblyId = spanAssemblyId;
                foundationConditions[span] = conditionData;
              }

              systemSetting.foundationConditions = foundationConditions;

              spanAssemblyIds[hash] = spanAssemblyId;
            } else if (spanAssemblyIds[hash]) {
              const spanAssemblyId = spanAssemblyIds[hash];

              const foundationConditions = systemSetting.foundationConditions ?? {};

              for (const span of spanFoundationConditions) {
                const conditionData = foundationConditions[span] ?? {};
                conditionData.assemblyId = spanAssemblyId;
                foundationConditions[span] = conditionData;
              }

              systemSetting.foundationConditions = foundationConditions;
            }

            delete systemSetting.footingWidth;
            delete systemSetting.spanFootingWidth;
            delete systemSetting.footingDepth;
            delete systemSetting.brickLedgeType;
            delete systemSetting.brickLedgeBlockType;
            delete systemSetting.stemWallBlockType;
            delete systemSetting.brickLedgeFootingPadding;
          }
        }
      }

      if (Object.keys(components).length) {
        const settingsSetId = uid();
        const settingsSet = {
          entityType: "SETTINGS_SET",
          name: "Foundation Catalog",
          id: settingsSetId,
          sortIndex: 0,
        };

        if (!json.settingsSets) {
          json.settingsSets = {};
        }

        json.settingsSets[settingsSetId] = settingsSet;

        const foundationCatalogSettingsId = uid();
        const foundationCatalogSettings = {
          id: foundationCatalogSettingsId,
          type: "FOUNDATION_CATALOG_SETTINGS",
          entityType: "SETTINGS",
          name: "Foundation Catalog Settings",
          sortIndex: 0,
          parentId: settingsSetId,
          foundationAssemblies: assemblies,
          foundationComponents: components,
        };

        json.settings[foundationCatalogSettingsId] = foundationCatalogSettings;
      }
    }

    return json;
  },
};

export default migration_0_0_162;
